import { getEnv } from "@bfc-software/bfc-web-ui/environment";
import { internalToolsEnabled } from "@bfc-software/bfc-web-ui/lib/internal-tools";

/**
 * The products an organisation can open from the picker.
 *
 * One list, one availability predicate each. Which tools an organisation may
 * open is a real access decision that isn't designed yet - `AuthUtils` already
 * carries `hasPermission` (role/permission) and `getFeatureFlag` (per-org
 * flags), and neither is wired to anything. When that model lands, it should
 * mean rewriting `isAvailable` here and nothing else.
 *
 * Until then availability is honest about being provisional rather than
 * pretending to check something.
 *
 * Titles and copy live in the portal translations, keyed by id - the design
 * treats them as content, and they should not be duplicated here.
 */

/** Products this app serves itself, each behind a route in its own tree. */
export type InternalTileId = "outbound" | "analytics" | "internal";

/** Products hosted elsewhere. The picker links out to these in a new tab. */
export type ExternalTileId = "loadBuilder";

export type TileId = InternalTileId | ExternalTileId;

export interface Tile {
  id: TileId;
  /** Which tools are ready to use, versus visible but not yet built out. */
  status: "open" | "coming-soon";
  isAvailable: () => boolean;
}

export const TILES: ReadonlyArray<Tile> = [
  { id: "outbound", status: "coming-soon", isAvailable: () => true },
  { id: "loadBuilder", status: "open", isAvailable: () => true },
  { id: "analytics", status: "open", isAvailable: () => true },
  { id: "internal", status: "open", isAvailable: internalToolsEnabled },
];

export function availableTiles(): ReadonlyArray<Tile> {
  return TILES.filter((tile) => tile.isAvailable());
}

/**
 * Where each externally hosted product lives.
 *
 * Resolved on call rather than at import, because the deployment target is read
 * from the environment when asked and a value captured at module load would
 * pin whichever target happened to be set first.
 */
const EXTERNAL_TILE_URLS = {
  loadBuilder: () => getEnv().loadBuilderUrl,
} satisfies Record<ExternalTileId, () => string>;

export function isExternalTile(id: TileId): id is ExternalTileId {
  return id in EXTERNAL_TILE_URLS;
}

export function externalTileUrl(id: ExternalTileId): string {
  return EXTERNAL_TILE_URLS[id]();
}
